// slice/adminSlice.ts
// ---------------------------------------------------------------------------
// Real API thunks for the Super Admin create-tenant / add-user flow.
// Talks to /api/v1/admin (the backend endpoints Vandana built).
// ---------------------------------------------------------------------------

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_V1_BASE_URL } from '../config/api';

const BASE = `${API_V1_BASE_URL}/admin`;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
    'Content-Type': 'application/json',
});

async function handle(res: Response) {
    const json = await res.json();
    if (!res.ok || json.success === false) {
        throw new Error(json.message || 'Request failed');
    }
    return json.data;
}

export interface TenantOption {
    id: string;
    name: string;
}

// GET /admin/tenants  -> [{ id, name }] for the dropdown
export const fetchTenants = createAsyncThunk(
    'admin/fetchTenants',
    async (_, { rejectWithValue }) => {
        try {
            const res = await fetch(`${BASE}/tenants`, { headers: authHeaders() });
            return (await handle(res)) as TenantOption[];
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

// POST /admin/tenants  -> creates tenant + first user (forced CUSTOMER_ADMIN)
export const registerTenant = createAsyncThunk(
    'admin/registerTenant',
    async (
        payload: {
            tenant: { name: string; subdomain: string; email: string; phoneNumber?: string; maxUsers?: number };
            user: { email: string; firstName: string; lastName: string; department?: string; phoneNumber?: string; password: string };
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await fetch(`${BASE}/tenants`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(payload),
            });
            return await handle(res);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

// POST /admin/users  -> adds a user to an existing tenant
export const createUser = createAsyncThunk(
    'admin/createUser',
    async (
        payload: {
            tenantId: string;
            role: string;
            email: string;
            firstName: string;
            lastName: string;
            department?: string;
            phoneNumber?: string;
            password: string;
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await fetch(`${BASE}/users`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(payload),
            });
            return await handle(res);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

interface AdminState {
    tenants: TenantOption[];
    loading: boolean;       // for submit actions
    tenantsLoading: boolean; // for the dropdown fetch
    error: string | null;
}

const initialState: AdminState = {
    tenants: [],
    loading: false,
    tenantsLoading: false,
    error: null,
};

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTenants.pending, (state) => { state.tenantsLoading = true; })
            .addCase(fetchTenants.fulfilled, (state, action) => {
                state.tenantsLoading = false;
                state.tenants = action.payload;
            })
            .addCase(fetchTenants.rejected, (state, action) => {
                state.tenantsLoading = false;
                state.error = (action.payload as string) || 'Failed to load tenants';
            })
            .addCase(registerTenant.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(registerTenant.fulfilled, (state) => { state.loading = false; })
            .addCase(registerTenant.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to create tenant';
            })
            .addCase(createUser.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(createUser.fulfilled, (state) => { state.loading = false; })
            .addCase(createUser.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to create user';
            });
    },
});

export const { clearError } = adminSlice.actions;
export default adminSlice.reducer;
